import { Avatar, Box, Link, Paper, Tooltip, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  IOpenProjectComment,
  IOpenProjectUserDetails,
} from "../../common/interface";
import { getOpenProjectLinksRes } from "../../services/openProject.service";
import HtmlContentRenderer from "../HtmlContentRenderer";

// CommentCard Component
const CommentCard: React.FC<{
  comment: IOpenProjectComment;
  user:
    | IOpenProjectUserDetails
    | {
        id: any;
        name: any;
        avatar: string;
        _links: { self: { href: string } };
      };
}> = ({ comment, user }) => {
  const isEdited = comment.createdAt !== comment.updatedAt;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    }).format(date);
  };

  const timeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const minutes = Math.floor(diffInSeconds / 60);
    const hours = Math.floor(diffInSeconds / 3600);
    const days = Math.floor(diffInSeconds / 86400);
    const months =
      now.getMonth() -
      date.getMonth() +
      12 * (now.getFullYear() - date.getFullYear());
    const years = now.getFullYear() - date.getFullYear();
    if (years > 0) return `${years} year${years > 1 ? "s" : ""} ago`;
    if (months > 0) return `${months} month${months > 1 ? "s" : ""} ago`;
    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    return `${diffInSeconds} second${diffInSeconds > 1 ? "s" : ""} ago`;
  };

  const userLink = comment?._links?.user?.href;
  const userId = userLink?.split("/").pop(); // Get user ID from the userLink

  return (
    <Paper variant="outlined" sx={{ padding: 2, marginBottom: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Avatar
          src={user?.avatar}
          alt={user?.name ?? userId}
          sx={{ width: 36, height: 36 }} // Smaller avatar for comments
        />
        <Box sx={{ marginLeft: 1 }}>
          <Typography variant="subtitle2" component="div">
            <Link
              className="pointer-events-none"
              href={user?._links?.self?.href}
              target="_blank"
              underline="hover"
              color="inherit" // Inherit color
              sx={{ fontWeight: "bold" }}
            >
              {user?.name ?? userId}
            </Link>
          </Typography>

          <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
            <Tooltip
              placement="top-start"
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#fff",
                    color: "#14274e",
                    boxShadow: "0px 2px 6px rgba(0,0,0,0.2)", // Softer shadow
                    padding: "8px 16px",
                    borderRadius: "6px",
                    textAlign: "center",
                    fontSize: "0.8rem",
                  },
                },
              }}
              id={`created_${comment.id}`}
              title={formatDate(comment.createdAt)}
            >
              <span> {timeAgo(comment.createdAt)} </span>
            </Tooltip>
            {isEdited && (
              <Tooltip
                placement="top-start"
                slotProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: "#fff",
                      color: "#14274e",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      textAlign: "center",
                      fontSize: "0.8rem",
                    },
                  },
                }}
                id={`updated_${comment.id}`}
                title={formatDate(comment.updatedAt)}
              >
                <Typography
                  component="span"
                  sx={{ ml: 0.5, fontStyle: "italic" }}
                >
                  (edited)
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        <HtmlContentRenderer
          key={comment?.id}
          htmlString={comment?.comment?.html ?? ""}
        />
      </Box>
    </Paper>
  );
};

interface IOPCommentSectionProps {
  comments: IOpenProjectComment[];
}

const OPCommentSection = ({ comments }: IOPCommentSectionProps) => {
  const [userDetailsMap, setUserDetailsMap] = useState<
    Map<string, IOpenProjectUserDetails>
  >(new Map());

  const getUserDetails = useCallback(
    async (userLink: string): Promise<IOpenProjectUserDetails | null> => {
      const existingUser = userDetailsMap.get(userLink);
      if (existingUser) {
        return existingUser; // Return cached user data
      }

      try {
        const userData = (await getOpenProjectLinksRes<IOpenProjectUserDetails>(
          {
            href: userLink,
          }
        )) as unknown as IOpenProjectUserDetails;
        if (userData?._type === "User") {
          setUserDetailsMap(
            (prevMap) => new Map(prevMap.set(userLink, userData))
          );
          return userData;
        }
      } catch (error) {
        console.error("Error fetching user details", error);
      }
      return null;
    },
    [userDetailsMap]
  );

  // Get unique user links from comments
  const uniqueUserLinks = Array.from(
    new Set(
      comments.map((comment) => comment._links?.user?.href).filter(Boolean)
    )
  );

  // Fetch user details for all unique user links
  useEffect(() => {
    const fetchUserDetails = async () => {
      for (const userLink of uniqueUserLinks) {
        await getUserDetails(userLink);
      }
    };

    if (uniqueUserLinks.length > 0) {
      fetchUserDetails();
    }
  }, [uniqueUserLinks, getUserDetails]);

  return (
    <Box sx={{ padding: 2 }}>
      {comments.length > 0 ? (
        comments.map((data) => {
          const userLink = data?._links?.user?.href;
          const user = userLink ? userDetailsMap.get(userLink) : null;
          const userId = userLink?.split("/").pop();

          return (
            <CommentCard
              key={data?.id}
              comment={data}
              user={
                user ?? {
                  id: userId,
                  name: userId,
                  avatar: "",
                  _links: { self: { href: "" } },
                }
              }
            />
          );
        })
      ) : (
        <Typography variant="h6" color="textSecondary">
          No comments for this ticket.
        </Typography>
      )}
    </Box>
  );
};

export default OPCommentSection;
